/**
 * Rating Modal Komponente
 * Implementiert das Modal zum Abgeben einer Bewertung
 */
import { canInteract, validateHuman, stopTracking, startTracking, isDebugMode } from '../utils/security.js'
import { createModalStars, updateStarsDisplay } from './RatingStars.js'
import { MAX_STARS } from '../utils/constants.js'

// Modal-DOM-Struktur
const MODAL_HTML = `
<div class="rw-modal-backdrop">
    <div class="rw-modal-container">
        <div class="rw-modal-header">
            <h3 class="rw-rating-header headline headline--3"></h3>
        </div>
        <div class="rw-modal-body">
            <div class="rw-rating-modal-content">
                <div class="bodytext">
                    <p class="rw-rating-description"></p>
                </div>
                <div class="rw-rating-stars-wrapper"></div>
                <div class="rw-error-container"></div>
                <div class="rw-submit-button-container">
                    <button class="rw-submit-button btn"></button>
                </div>
            </div>
        </div>
    </div>
</div>
`

export class RatingModal {
    constructor(options = {}) {
        this.options = {
            currentRating: options.currentRating || 0,
            maxStars: options.maxStars || MAX_STARS,
            ratingPid: options.ratingPid,
            onSubmit: options.onSubmit || null,
            translations: options.translations || {
                modalTitle: '',
                submitButton: '',
                cancelButton: '',
                tooltip: '',
                header: '',
                description: '',
                botError: '',
            },
        }

        this.userRating = 5 // Start mit 5 Sternen
        this.modalElement = null
        this.backdropElement = null
        this.starsContainer = null
        this.errorContainer = null
        this.submitButton = null
        this.validationAttempts = 0

        // Modal erstellen und vorbereiten
        this._createModal()
    }

    /**
     * Erstellt das Modal und fügt es dem DOM hinzu
     * @private
     */
    _createModal() {
        // Container für das Modal erstellen
        const container = document.createElement('div')
        container.innerHTML = MODAL_HTML.trim()

        // Elemente speichern
        this.backdropElement = container.querySelector('.rw-modal-backdrop')
        this.modalElement = container.querySelector('.rw-modal-container')
        this.errorContainer = container.querySelector('.rw-error-container')
        this.submitButton = container.querySelector('.rw-submit-button')

        // Modal-Titel setzen
        //const titleElement = this.modalElement.querySelector('.rw-modal-title')
        //titleElement.textContent = this.options.translations.modalTitle

        // Header und Beschreibung setzen
        const headerElement = this.modalElement.querySelector('.rw-rating-header')
        headerElement.textContent = this.options.translations.header

        const descriptionElement = this.modalElement.querySelector('.rw-rating-description')
        descriptionElement.textContent = this.options.translations.description

        // Submit button einrichten
        this.submitButton.textContent = this.options.translations.submitButton
        this.submitButton.addEventListener('click', () => this.submitRating())

        // Stern-Container vorbereiten
        const starsWrapper = this.modalElement.querySelector('.rw-rating-stars-wrapper')
        this.starsContainer = createModalStars(
            5, // Start mit 5 Sternen
            this.options.maxStars,
            (rating) => this._handleStarClick(rating),
            null // Kein Hover-Handler mehr nötig
        )
        starsWrapper.appendChild(this.starsContainer)

        // Close button removed

        // Backdrop-Click-Event
        this.backdropElement.addEventListener('click', (event) => {
            if (event.target === this.backdropElement) {
                this.close()
            }
        })

        // ESC-Taste zum Schließen
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.isOpen()) {
                this.close()
            }
        })

        // Event-Listener für Mausbewegungen im Modal hinzufügen
        this.modalElement.addEventListener('mousemove', () => {
            // Tracking registrieren, wenn das Modal offen ist
            startTracking()
        })

        // Dokument-Body hinzufügen
        document.body.appendChild(container.firstChild)

        // Initial ausblenden
        this.backdropElement.style.display = 'none'
    }

    /**
     * Verarbeitet den Klick auf einen Stern
     * @param {number} rating - Die gewählte Bewertung
     * @private
     */
    _handleStarClick(rating) {
        // Prüfen, ob Interaktion möglich ist (nach Verzögerung)
        if (!canInteract()) {
            const remainingTime = Math.round((Date.now() - new Date().getTime()) / 1000)
            this._showError(`Bitte warten Sie noch ${remainingTime} Sekunden, bevor Sie bewerten.`)
            return
        }

        // Bewertung speichern
        this.userRating = rating
    }

    /**
     * Zeigt einen Fehler im Modal an
     * @param {string} message - Die Fehlermeldung
     * @private
     */
    _showError(message) {
        if (!this.errorContainer) return

        this.errorContainer.textContent = message
        this.errorContainer.style.display = 'block'

        // Fehler nach einiger Zeit ausblenden
        setTimeout(() => {
            if (this.errorContainer) {
                this.errorContainer.style.display = 'none'
            }
        }, 5000)
    }

    /**
     * Sendet die Bewertung
     */
    submitRating() {
        if (!this.userRating) {
            this._showError('Bitte wähle zuerst eine Bewertung aus.')
            return
        }

        // Validieren, ob es ein Mensch ist
        if (validateHuman()) {
            // Fehleranzeige zurücksetzen
            if (this.errorContainer) {
                this.errorContainer.style.display = 'none'
            }

            // Callback aufrufen, falls vorhanden
            if (typeof this.options.onSubmit === 'function') {
                this.options.onSubmit(this.userRating)
            }

            // Modal schließen
            this.close()
        } else {
            this.validationAttempts++

            // Fehlermeldung anzeigen
            const errorMessage =
                this.validationAttempts > 1
                    ? 'Bitte bewegen Sie Ihre Maus und interagieren Sie mit der Seite, um zu bestätigen, dass Sie ein Mensch sind.'
                    : this.options.translations.botError || 'Unable to verify human interaction. Please try again.'

            this._showError(errorMessage)

            // Im Debug-Modus Hilfestellungen anzeigen
            if (isDebugMode()) {
                // Tracking starten, um die Validierung zu erleichtern
                startTracking()
            }
        }
    }

    /**
     * Öffnet das Modal
     * @returns {RatingModal} - Sich selbst für Method-Chaining
     */
    open() {
        // Prüfen, ob Interaktion möglich ist (nach Verzögerung)
        if (!canInteract()) {
            return this
        }

        // Tracking starten
        startTracking()

        // Validierungsversuche zurücksetzen
        this.validationAttempts = 0

        // Fehlermeldung zurücksetzen
        if (this.errorContainer) {
            this.errorContainer.style.display = 'none'
        }

        // Benutzer-Rating auf 5 (alle Sterne) setzen
        this.userRating = 5

        // Sterne auf 5 setzen
        updateStarsDisplay(this.starsContainer, 5)

        // Modal anzeigen
        this.backdropElement.style.display = 'block'
        this.backdropElement.classList.add('rw-modal-open')
        this.modalElement.classList.add('rw-modal-open')

        // Scrolling verhindern
        document.body.style.overflow = 'hidden'

        return this
    }

    /**
     * Schließt das Modal
     * @returns {RatingModal} - Sich selbst für Method-Chaining
     */
    close() {
        if (this.backdropElement) {
            this.backdropElement.classList.remove('rw-modal-open')
            this.modalElement.classList.remove('rw-modal-open')

            // Nach Fadeout-Animation das Element ausblenden
            setTimeout(() => {
                this.backdropElement.style.display = 'none'
                // Scrolling wieder erlauben
                document.body.style.overflow = ''
            }, 300)
        }

        return this
    }

    /**
     * Prüft, ob das Modal geöffnet ist
     * @returns {boolean} - true wenn offen, false wenn geschlossen
     */
    isOpen() {
        return this.backdropElement && this.backdropElement.style.display !== 'none'
    }

    /**
     * Factory-Methode zum schnellen Anzeigen des Modals
     * @param {Object} options - Optionen für das Modal
     * @returns {RatingModal} - Die erstellte Modal-Instanz
     */
    static show(options) {
        const modal = new RatingModal(options)
        modal.open()
        return modal
    }
}
